import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import router from './router';
// Ensure Firebase is initialized before any feature (auth/api/rewards) calls getAuth().
import './lib/firebase';
import './index.css';

const RAW_BUILD_ID =
  import.meta.env.VITE_COMMIT_SHA ||
  import.meta.env.VITE_BUILD_ID ||
  '';
const IMPORT_META_FINGERPRINT = (() => {
  try {
    const raw = String(import.meta.url || '').trim();
    if (!raw) return '';
    const tail = raw.split('/').pop() || raw;
    return tail.replace(/[^a-z0-9._-]+/gi, '_') || raw;
  } catch {
    return '';
  }
})();
const BUILD_ID =
  (RAW_BUILD_ID && !RAW_BUILD_ID.startsWith('$') ? RAW_BUILD_ID : '') ||
  IMPORT_META_FINGERPRINT ||
  'unknown';
try {
  (window as any).__BW_BUILD_ID__ = BUILD_ID;
  (window as any).__BW_DEV__ = import.meta.env.DEV;
} catch {}
console.log('[build] id', BUILD_ID);

const logCrash = (tag: string, payload: Record<string, unknown>) => {
  try {
    console.error(tag, payload);
    if (import.meta.env.DEV) return;
    localStorage.setItem('bw:lastCrash', JSON.stringify(payload));
  } catch {}
};

const CHUNK_RECOVERY_KEY = 'bw:chunk-recovery:last';

const isDynamicImportChunkError = (message: string) => {
  const text = String(message || '').toLowerCase();
  return (
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('importing a module script failed') ||
    text.includes('error loading dynamically imported module')
  );
};

const recoverFromStaleChunkOnce = async (message: string) => {
  if (!isDynamicImportChunkError(message)) return false;
  try {
    const marker = `${window.location.pathname}::${BUILD_ID}`;
    const last = sessionStorage.getItem(CHUNK_RECOVERY_KEY);
    if (last === marker) return false;
    sessionStorage.setItem(CHUNK_RECOVERY_KEY, marker);
  } catch {}

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.update().catch(() => {})));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((key) => key.includes('bitewise-shell'))
          .map((key) => caches.delete(key).catch(() => false))
      );
    }
  } catch {}

  const next = new URL(window.location.href);
  next.searchParams.set('refresh', String(Date.now()));
  window.location.replace(next.toString());
  return true;
};

const getSwInfo = () => {
  try {
    const controller = navigator.serviceWorker?.controller;
    return {
      scriptURL: controller?.scriptURL || null,
      state: controller?.state || null,
    };
  } catch {
    return { scriptURL: null, state: null };
  }
};

const captureGlobalErrors = () => {
  window.addEventListener('error', (ev) => {
    const message = ev?.message || 'Unknown error';
    void recoverFromStaleChunkOnce(message);
    const payload = {
      type: 'error',
      message,
      stack: (ev as any)?.error?.stack,
      filename: (ev as any)?.filename,
      lineno: (ev as any)?.lineno,
      colno: (ev as any)?.colno,
      href: window.location.href,
      pathname: window.location.pathname,
      buildId: BUILD_ID,
      userAgent: navigator.userAgent,
      sw: getSwInfo(),
    };
    logCrash('[crash] error', payload);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason: any = (ev as any)?.reason;
    const message = reason?.message || String(reason || 'Unhandled rejection');
    void recoverFromStaleChunkOnce(message);
    const payload = {
      type: 'unhandledrejection',
      message,
      stack: reason?.stack,
      href: window.location.href,
      pathname: window.location.pathname,
      buildId: BUILD_ID,
      userAgent: navigator.userAgent,
      sw: getSwInfo(),
    };
    logCrash('[crash] unhandledrejection', payload);
  });
};

const logAssetList = async () => {
  try {
    const url = window.location.href;
    const resp = await fetch(url, { cache: 'no-store' });
    const html = await resp.text();
    const assets = Array.from(new Set(html.match(/\/assets\/[^"']+\.js/g) || []))
      .map((path) => path.split('/').pop())
      .filter(Boolean);
    const currentScript = document.currentScript instanceof HTMLScriptElement
      ? document.currentScript.src
      : null;
    console.debug('[assets] list', {
      buildId: BUILD_ID,
      currentScript,
      assets,
      ts: Date.now(),
    });
  } catch {}
};

captureGlobalErrors();

if (import.meta.env.DEV || new URLSearchParams(window.location.search).get('debug') === '1') {
  void logAssetList();
}

if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    console.debug('[sw] controllerchange', {
      scriptURL: navigator.serviceWorker?.controller?.scriptURL || null,
      ts: Date.now(),
    });
  });
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (!reg) return;
    reg.addEventListener('updatefound', () => {
      console.debug('[sw] updatefound', { scope: reg.scope, ts: Date.now() });
    });
  });
}

const ErrorFallback = () => (
  <div className="min-h-dvh grid place-items-center bg-[#020617] text-white px-4">
    <div className="max-w-md text-center space-y-3">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm text-white/70">
        We hit an unexpected error. Please reload to continue.
      </p>
      <button
        className="mt-2 rounded-xl bg-white text-black px-4 py-2 font-semibold"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    </div>
  </div>
);

ReactDOM.createRoot(document.getElementById('root')!).render(<RouterProvider router={router} />);
